import { breakpoints, Button } from "@brightagency/headstart-common";
import CloseRounded from "@mui/icons-material/CloseRounded";
import Devices from "@mui/icons-material/Devices";
import EmailIcon from "@mui/icons-material/Email";
import LocalPhoneRounded from "@mui/icons-material/LocalPhoneRounded";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import Paid from "@mui/icons-material/Paid";
import PeopleRoundedIcon from "@mui/icons-material/PeopleRounded";
import VerifiedIcon from "@mui/icons-material/Verified";
import React from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router";
import styled from "styled-components";
import { siteData } from "../../../../../constants";
import { ANALYTICS_IDS } from "../../../../../constants/analytics";
import { CoordType, WPPost } from "../../../../../constants/types";
import {
    CostOfService,
    getCopyForCostOfService,
    getCopyForReferralOption,
    ListingAcf,
    ListingLocation,
    ReferralOption,
} from "../../../../../constants/types/acf";
import { getLocationDistanceFromUser, useSuburbName } from "../../../../../utils";
import { colors } from "../../../../../utils/colors";
import { useListingDistance, useSortingCoords } from "../../../../../utils/hooks";
import { RichText } from "../../../../atoms/RichText";
import { SaveItemButton } from "../../../../atoms/SaveItemButton";
import { SortDistanceButton } from "../../../../atoms/SortDistanceButton";
import { UnstyledLink } from "../../../../atoms/UnstyledLink";
import { useListingModal } from "../../controller";
import { Feedback } from "./../Feeback";
import { Location } from "./../Location";
import {
    Inner,
    Header,
    Content,
    Title,
    TitleBar,
    Distance,
    Buttons,
    Tags,
    Tag,
    CloseButton,
    SaveItemButtonWrapper,
} from "./styled";

function isDarkColor(color: string) {
    if (!(color.includes("#") && color.length === 7)) {
        console.error(`isDarkColor() only works with hex colors. Provided string: "${color}"`);
        return true;
    }
    const hex = color.replace("#", "");
    const red = parseInt(hex.substring(0, 2), 16);
    const green = parseInt(hex.substring(2, 4), 16);
    const blue = parseInt(hex.substring(4, 6), 16);
    // Returns a brightness between 0-255 (0 == dark, 255 == light)
    const brightness = (red * 299 + green * 587 + blue * 114) / 1000;
    // 155 seems to hit a good balance of light/dark
    return brightness < 155;
}

interface Props {
    listing: WPPost<ListingAcf>;
    hideCloseButton?: boolean;
}

const LocationsTitle = styled.h3`
    position: relative;
    text-align: center;
    padding: 56px 0 32px 0;
    margin-left: -30px;
    margin-right: -30px;
    font-size: 25px;
    color: ${colors.brandDark};

    ${breakpoints.large} {
        margin-left: auto;
        margin-right: auto;
        font-size: 32px;
    }
`;

function filterLocationsByRadius(locations: ListingLocation[], sortingCoords: CoordType | undefined){
    const filteredLocations:ListingLocation[] = [];
    for (const location of locations) {
        if (sortingCoords) {
            const distance = getLocationDistanceFromUser(location, sortingCoords);
            if (distance <= 100) {
                filteredLocations.push(location);
            }
        } else {
            filteredLocations.push(location);
        }
    }
    return filteredLocations;
}

export const ListingItemContent: React.FC<Props> = ({ listing: data, hideCloseButton = false }) => {
    const sortingCoords = useSortingCoords();

    let hasLocations = (data?.acf?.locations || []).length > 0;
    let filteredLocations = (data.acf.locations || []);
    if (data?.acf?.locations) {
        filteredLocations = filterLocationsByRadius(data.acf.locations, sortingCoords);
        hasLocations = (filteredLocations || []).length > 0;
    }
    
    const { closeListing } = useListingModal();
    const distanceString = useListingDistance(data);
    const location = useLocation();
    const suburbName = useSuburbName();

    // Sort locations by distance to user if available
    const locations = filteredLocations.sort((a, b) => {
        if (sortingCoords) {
            const distanceA = getLocationDistanceFromUser(a, sortingCoords);
            const distanceB = getLocationDistanceFromUser(b, sortingCoords);
            if (distanceA < distanceB) return -1;
            if (distanceA > distanceB) return 1;
        }
        return 0;
    });

    const { cost_of_service, referral } = data.acf.meta;

    const canonical = new URL(siteData.siteURL + location.pathname);
    canonical.hash = `listing=${data.slug}`;
    canonical.search = location.search;

    return (
        <>
            <Helmet>
                <title>{data.title.rendered}</title>
                <link rel="canonical" href={canonical.toString()} />
            </Helmet>
            <Inner>
                <Header
                    style={{
                        backgroundColor: data.acf.meta.logo_background_colour,
                    }}
                >
                    {!hideCloseButton && (
                        <CloseButton
                            onClick={closeListing}
                            style={{
                                color: isDarkColor(data.acf.meta.logo_background_colour)
                                    ? colors.white
                                    : colors.brandDark,
                            }}
                        >
                            <CloseRounded />
                        </CloseButton>
                    )}
                    <img src={data.acf.meta.logo.sizes?.large ?? ""} alt="" />
                    <SaveItemButtonWrapper>
                        <SaveItemButton id={data.id} size="large" />
                    </SaveItemButtonWrapper>
                </Header>
                <Content>
                    <TitleBar>
                        <div>
                            <Title>{data.title.rendered}</Title>
                            {distanceString && (
                                <>
                                    <Distance>{distanceString}</Distance>
                                    {suburbName && 
                                        <SortDistanceButton />
                                    }
                                </>
                            )}
                        </div>
                    </TitleBar>
                    {(cost_of_service !== CostOfService.NotApplicable ||
                        referral !== ReferralOption.NotApplicable ||
                        data.acf.meta.service_provided_by !== "") && (
                        <Tags>
                            {cost_of_service !== CostOfService.NotApplicable && (
                                <Tag>
                                    <Paid />
                                    {getCopyForCostOfService(cost_of_service)}
                                </Tag>
                            )}
                            {referral !== ReferralOption.NotApplicable && (
                                <Tag>
                                    <PeopleRoundedIcon />
                                    {getCopyForReferralOption(referral)}
                                    {referral === ReferralOption.GPReferral && (
                                        <UnstyledLink
                                            href="https://www.healthdirect.gov.au/australian-health-services"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Find a GP
                                        </UnstyledLink>
                                    )}
                                </Tag>
                            )}
                            {data.acf.meta.service_provided_by && (
                                <Tag>
                                    <VerifiedIcon />
                                    Service provided by {data.acf.meta.service_provided_by}
                                </Tag>
                            )}
                            {data.acf.also_available_by_phone_or_online && (
                                <Tag>
                                    <Devices />
                                    Also available by phone or online
                                </Tag>
                            )}
                        </Tags>
                    )}
                    <RichText content={data.acf.content.description} />
                    <Buttons>
                        {data.acf.meta.phone_number && (
                            <UnstyledLink href={`tel:${data.acf.meta.phone_number}`}>
                                <Button
                                    data-analytics-id={ANALYTICS_IDS.servicePhoneNumber}
                                    appearance="secondary"
                                    style={{ marginTop: 24 }}
                                    iconBefore={<LocalPhoneRounded />}
                                >
                                    Call {data.acf.meta.phone_number}
                                </Button>
                            </UnstyledLink>
                        )}
                        {data.acf.meta.email_address && (
                            <UnstyledLink href={`mailto:${data.acf.meta.email_address}`}>
                                <Button
                                    data-analytics-id={ANALYTICS_IDS.serviceLocationEmail}
                                    appearance="secondary"
                                    style={{ marginTop: 24 }}
                                    iconBefore={<EmailIcon />}
                                >
                                    Send email
                                </Button>
                            </UnstyledLink>
                        )}
                        {data.acf.meta.website && (
                            <UnstyledLink
                                href={data.acf.meta.website}
                                target="_blank"
                                rel="noreferrer noopener"
                            >
                                <Button
                                    data-analytics-id={ANALYTICS_IDS.serviceWebsite}
                                    style={{ marginTop: 24 }}
                                    iconBefore={<OpenInNewOutlinedIcon />}
                                >
                                    Visit website
                                </Button>
                            </UnstyledLink>
                        )}
                    </Buttons>
                    { suburbName && (
                        <>
                            {hasLocations && (
                                <LocationsTitle>Locations near {suburbName}</LocationsTitle>
                            )}
                            {locations.map((loc, index) => (
                                <Location key={index} location={loc} />
                            ))}
                        </>
                    )}
                    <Feedback regionName={data.title.rendered} />
                </Content>
            </Inner>
        </>
    );
};
